import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useCarDriversApi } from "../../http/carDrivers/api";
import { Car, getDelegationLanguages, getTypesOfUse, TypeOfUse } from "../../http/cars/types";
import { Language, Subsidiary } from "../../http/subsidiaries/types";
import { useUsersApi } from "../../http/users/api";
import { User } from "../../http/users/types";
import CarSubsidiariesDialog from "../CarSubsidiaries/CarSubsidiariesDialog";
import { ErrorBox } from "../ui/Boxes";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import MultipleInput from "../ui/MultipleInput";
import reactSelectStyles from "../ui/ReactSelectUtils";
import SubmitBar from "../ui/SubmitBar";

const USER_FIELDS = ["id", "fullName"] as const;
type UserField = (typeof USER_FIELDS)[number];
type UserSearch = Pick<User, UserField>;

export interface DelegationFormBody {
  driver?: UserSearch;
  driversName: string[];
  typeOfUse: TypeOfUse;
  language: Language;
}

type DelegationFormProps = {
  onSubmit: (form: DelegationFormBody) => Promise<void>;
  isLoading?: boolean;
  carDriverId?: string;
  car?: Car;
  external?: boolean;
  onUpdate?: () => Promise<void>;
};

const DelegationForm: FC<DelegationFormProps> = ({
  onSubmit,
  carDriverId,
  car,
  external,
  onUpdate,
}: DelegationFormProps) => {
  const i18n = useI18n();
  const usersApi = useUsersApi();
  const carDriversApi = useCarDriversApi();
  const aborter = useRef(new AbortController());

  const [defaultUserOptions, setDefaultUserOptions] = useState<UserSearch[]>([]);
  const [isDriversValid, setIsDriversValid] = useState<boolean>(false);
  const [showCarSubsidiariesDialog, setShowCarSubsidiariesDialog] = useState(false);

  const types = getTypesOfUse(i18n.t);
  const languages = getDelegationLanguages(i18n.t);

  const { data, trigger } = carDriversApi.getAdminCarDriver(carDriverId!, aborter.current.signal);

  const carLocal = data?.car ?? car;
  const subsidiaryLocal = data?.car?.carSubsidiary?.subsidiary ?? car?.carSubsidiary?.subsidiary;
  const isDisabledLocal = !subsidiaryLocal;

  useEffect(() => {
    carDriverId && trigger();
  }, [carDriverId]);

  const defaultValues = {
    driver: data?.driver,
    typeOfUse: !!data
      ? types.find((x) => x.value === data?.typeOfUse)
      : external
        ? types.find((x) => x.value == "private")
        : undefined,
    language: languages.find((x) => x.value == subsidiaryLocal?.defaultLanguage),
  };

  return (
    <Form<DelegationFormBody>
      submitText={i18n.t.common.create}
      disabled={isDisabledLocal}
      onSubmit={() => onSubmit}
      defaultValues={defaultValues}
    >
      {({ control, register, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [data]);

        const driversName = watch("driversName") ?? [];

        useEffect(() => {
          for (var i = 0; i < driversName.length; i++) {
            if (driversName[i] == "") {
              setIsDriversValid(false);
            } else {
              setIsDriversValid(true);
            }
          }
        }, [driversName, driversName.length]);

        return (
          <>
            <FormFieldsContainer>
              <FormField label={i18n.t.subsidiaries.singular}>
                <Select
                  menuPosition="fixed"
                  isDisabled
                  value={subsidiaryLocal}
                  getOptionLabel={(x) => x.name}
                  getOptionValue={(x) => x.id}
                  styles={reactSelectStyles<Subsidiary>()}
                />
              </FormField>
              {!!carLocal && !subsidiaryLocal && (
                <SubmitBar>
                  <ErrorBox text={i18n.t.common.subsidiaryRequiredForDelegation} />
                  <Button buttonProps={{ onClick: () => setShowCarSubsidiariesDialog(true) }}>
                    {i18n.t.common.assignSubsidiary}
                  </Button>
                </SubmitBar>
              )}
              {!external ? (
                <Controller
                  name="driver"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <FormField label={i18n.t.drivers.singular} error={fieldState.error}>
                      {({ labelId, isInvalid }) => {
                        const searchTerm = useRef("");
                        const aborter = useRef(new AbortController());

                        const loadOptions = createLoadOptions<UserSearch, UserField>(
                          aborter,
                          searchTerm,
                          usersApi.getAdminUsers,
                          { fields: USER_FIELDS },
                        );

                        // Fetch initial data on mount
                        useEffect(() => {
                          loadOptions(searchTerm.current).then(setDefaultUserOptions);
                        }, []);

                        return (
                          <AsyncSelect
                            {...field}
                            inputId={labelId}
                            isDisabled={isDisabledLocal || !!data}
                            defaultOptions={defaultUserOptions}
                            loadOptions={loadOptions}
                            placeholder={`${i18n.t.common.select}...`}
                            getOptionLabel={(x) => x.fullName}
                            getOptionValue={(x) => x.id}
                            styles={reactSelectStyles<UserSearch>(isInvalid)}
                            menuPosition="fixed"
                          />
                        );
                      }}
                    </FormField>
                  )}
                />
              ) : (
                <Controller
                  name="driversName"
                  control={control}
                  rules={{
                    required: true,
                    validate: () => {
                      if (!isDriversValid) {
                        return i18n.t.common.enterValidNames;
                      }
                      return undefined;
                    },
                  }}
                  render={({ field, fieldState }) => {
                    register("driversName", { required: true });

                    const isInvalid =
                      !isDisabledLocal && fieldState.invalid
                        ? (field.value ?? []).map((value) => value.trim() === "")
                        : undefined;

                    return (
                      <FormField label={i18n.t.drivers.plural} error={fieldState.error}>
                        <MultipleInput
                          onChange={(v) => setValue("driversName", v)}
                          disabled={isDisabledLocal}
                          isInvalid={isInvalid}
                        />
                      </FormField>
                    );
                  }}
                />
              )}
              <Controller
                name="typeOfUse"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.type} error={fieldState.error} hidden={external}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={types}
                        isDisabled={isDisabledLocal || !!data || !!external}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<TypeOfUse>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
              {external && (
                <FormField label={i18n.t.common.type}>
                  <Input value={i18n.t.common.private} disabled />
                </FormField>
              )}
              <Controller
                name="language"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.language} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        menuPosition="fixed"
                        {...field}
                        inputId={labelId}
                        options={languages}
                        isDisabled={isDisabledLocal}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        styles={reactSelectStyles<Language>(isInvalid)}
                      />
                    )}
                  </FormField>
                )}
              />
            </FormFieldsContainer>

            {!!showCarSubsidiariesDialog && !!carLocal && (
              <CarSubsidiariesDialog
                carId={carLocal.id}
                onClose={() => setShowCarSubsidiariesDialog(false)}
                onUpdate={async () => {
                  await onUpdate?.();
                  trigger();
                }}
              />
            )}
          </>
        );
      }}
    </Form>
  );
};

export default DelegationForm;
